import { ActiveItemWrapper } from '@/components/features/Editor/components/ActiveItemWrapper';
import { type SectionProps } from '@/components/features/Editor/utils';
import { Image } from '@/components/shared/Image';
import { useRefScrollProgress } from '@/hooks/useRefScrollProgress';
import { ReservoirChain } from '@/utils/chains';
import { AspectRatio, SimpleGrid, useBreakpointValue } from '@chakra-ui/react';
import { MotionBox } from '@sphere/ui';
import {
  useMotionValueEvent,
  useScroll,
  useSpring,
  useTransform,
  type MotionValue,
} from 'framer-motion';
import { useMemo, useRef, useState, type RefObject } from 'react';

const getXOffset = (row: number, column: number, isMobile: boolean): number => {
  const multiplier = row + 1;

  switch (column) {
    case 0:
      return (isMobile ? -50 : -150) * multiplier;
    case 1:
      return (isMobile ? 50 : -50) * multiplier;
    case 2:
      return 50 * multiplier;
    case 3:
      return 150 * multiplier;
    default:
      return 0;
  }
};

interface NftRowProps {
  tokenImages?: string[];
  groupIndex: number;
  increment: number;
  scrollProgress: MotionValue<number>;
  isPreview?: boolean;
  activeItem?: string;
}

const NftRow = ({
  tokenImages,
  groupIndex,
  isPreview,
  activeItem,
  increment,
  scrollProgress,
}: NftRowProps) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [offset, setOffset] = useState(1);
  const offsetValue = useTransform(scrollProgress, [0, (groupIndex + 1) * increment], [1, 0]);

  const offsetValueWithSpring = useSpring(offsetValue, {
    stiffness: 300,
    damping: 40,
    restDelta: 0.001,
  });

  useMotionValueEvent(offsetValueWithSpring, 'change', (offset: number) => setOffset(offset));

  return (
    <>
      {tokenImages?.map((tokenImage, index) => (
        <ActiveItemWrapper
          key={tokenImage + groupIndex + index}
          isActive={activeItem === String(index + groupIndex * (isMobile ? 2 : 4))}
          isPreview={isPreview}
        >
          <MotionBox
            animate={{
              translateX: isPreview ? 0 : `${getXOffset(groupIndex, index, !!isMobile) * offset}%`,
              transition: { duration: 0 },
            }}
          >
            <AspectRatio ratio={1 / 1}>
              <Image
                src={tokenImage}
                alt=""
                objectFit="contain"
                fill
                priority
                pointerEvents={isPreview ? 'none' : undefined}
              />
            </AspectRatio>
          </MotionBox>
        </ActiveItemWrapper>
      ))}
    </>
  );
};

interface Props extends SectionProps {
  address?: string;
  chain?: ReservoirChain;
  containerRef?: RefObject<HTMLElement>;
  activeItem?: string;
  isPreview?: boolean;
}

export const NftGalleryBlock = ({ containerRef, tokenIds, activeItem, isPreview }: Props) => {
  const isMobile = useBreakpointValue({ base: true, md: false });

  const scrollProgressContainer = useRef(null);
  const [start, end] = useRefScrollProgress(scrollProgressContainer);
  const { scrollYProgress: viewPortScrollYProgress } = useScroll(
    containerRef ? { container: containerRef, target: scrollProgressContainer } : {},
  );
  const scrollProgress = useTransform(viewPortScrollYProgress, [start || 0, end || 0], [0, 1]);

  const splitTokens = useMemo(
    () =>
      tokenIds?.reduce((acc: string[][], curr) => {
        acc.length > 0 || acc.push([]);
        acc[acc.length - 1].length === (isMobile ? 2 : 4)
          ? acc.push([curr])
          : acc[acc.length - 1].push(curr);
        return acc;
      }, []),
    [tokenIds, isMobile],
  );

  const increment = useMemo(
    () => (splitTokens?.length ? 1 / splitTokens.length : 1),
    [splitTokens],
  );

  return (
    <SimpleGrid
      columns={{ base: 2, md: 4 }}
      gap={{ base: 'space.16', md: 'space.24', lg: 'space.32' }}
      pt={containerRef ? '100vh' : { base: 'space.40', md: 'space.96' }}
      pb={{ base: 'space.40', md: 'space.96' }}
      px={{ base: 'space.16', md: 'space.32' }}
      bg="black"
      ref={scrollProgressContainer}
      overflow="hidden"
    >
      {splitTokens?.map((tokenImages, index) => (
        <NftRow
          key={index}
          tokenImages={tokenImages}
          groupIndex={index}
          isPreview={isPreview}
          activeItem={activeItem}
          increment={increment}
          scrollProgress={containerRef ? viewPortScrollYProgress : scrollProgress}
        />
      ))}
    </SimpleGrid>
  );
};
